import type { BonesMap, LoadedCharacterMesh } from '@cartwhl/3d/gltf/types';
import {
  disableFrustumCulled,
  hideMeshMaterials,
  turnOnShadows,
} from '@cartwhl/3d/gltf/utils';
import * as THREE from 'three';
import { FBXLoader } from 'three/examples/jsm/loaders/FBXLoader.js';

import { createSignedUrlLoadingManager } from '../utils/signedUrlCache';

/**
 * Resolves bones map path from mesh path (e.g. model.glb -> model.json, model.fbx -> model.json).
 */
function getBonesMapPath(meshPath: string): string {
  return meshPath.replace(/\.(glb|gltf|fbx)$/i, '.json');
}

/**
 * Fetches bones map JSON with credentials (same as GLTF flow).
 */
export async function fetchBonesMapWithCredentials(
  meshPath: string,
): Promise<BonesMap> {
  const bonesMapPath = getBonesMapPath(meshPath);
  try {
    const res = await fetch(bonesMapPath, { credentials: 'include' });
    if (!res.ok) return {};
    const data = await res.json();
    return data.joints ?? {};
  } catch {
    return {};
  }
}

/**
 * Load FBX with same credential and caching behavior as GLTF:
 * uses LoadingManager for signed-URL cache and fetches with credentials.
 * Returns a LoadedCharacterMesh so Character/utils can treat FBX like GLTF.
 */
export async function loadAndEnrichFBX(
  modelPath: string,
): Promise<LoadedCharacterMesh> {
  const manager = createSignedUrlLoadingManager();
  const loader = new FBXLoader(manager);
  loader.setWithCredentials(true);

  const scene = await new Promise<THREE.Group>((resolve, reject) => {
    loader.load(
      modelPath,
      (group) => resolve(group),
      undefined,
      (err) => reject(err),
    );
  });

  const bonesMap = await fetchBonesMapWithCredentials(modelPath);

  scene.traverse((object: THREE.Object3D) => {
    if (object instanceof THREE.Bone && object.name) {
      const originalName = bonesMap[object.name] ?? object.name;
      object.userData.originalName = originalName;
      object.userData.autoAssign = object.name;
    }
  });

  const nodes: { [key: string]: THREE.Object3D } = {};
  const materials: { [key: string]: THREE.Material } = {};
  const meshes: { [key: string]: THREE.Mesh } = {};

  scene.traverse((object: THREE.Object3D) => {
    if (object.name) nodes[object.name] = object;
    const mesh = object as THREE.Mesh;
    if (mesh.material) {
      const mat = mesh.material as THREE.Material;
      if (mat.name) materials[mat.name] = mat;
    }
    if (object instanceof THREE.Mesh && object.name) {
      meshes[object.name] = object;
    }
  });

  const result: LoadedCharacterMesh = {
    scene,
    nodes,
    materials,
    meshes,
    bonesMap,
  };

  // Apply same scene modifiers as GLTF path (shadows, frustum, hide materials)
  hideMeshMaterials(result);
  turnOnShadows(result);
  disableFrustumCulled(result);

  return result;
}
